import keys from "config/keys.jsx";

export const ACCESS_TOKEN_TTL_MS = 15 * 60 * 1000;
export const REFRESH_LEAD_TIME_MS = 60 * 1000;
export const REFRESH_LOCK_TTL_MS = 15 * 1000;
export const ACCESS_ISSUED_AT_KEY = "authSession:accessIssuedAt";
export const REFRESH_LOCK_KEY = "authSession:refreshLock";
export const SESSION_EXPIRED_EVENT = "avps-session-expired";
export const ACCESS_REFRESHED_EVENT = "avps-access-token-refreshed";

const REFRESH_ENDPOINT = "/api/auth/refresh-token";
const REFRESH_CHANNEL_NAME = "avps-auth-session";

let refreshPromise = null;
let broadcastChannel = null;

const getStorage = () => {
  try {
    return typeof window !== "undefined" ? window.localStorage : null;
  } catch {
    return null;
  }
};

const readNumber = (key) => {
  const value = Number(getStorage()?.getItem(key));
  return Number.isFinite(value) && value > 0 ? value : 0;
};

const readJson = (key) => {
  try {
    const value = getStorage()?.getItem(key);
    return value ? JSON.parse(value) : null;
  } catch {
    return null;
  }
};

const writeJson = (key, value) => {
  try {
    getStorage()?.setItem(key, JSON.stringify(value));
  } catch {
    // Storage can be unavailable in private browsing or restricted contexts.
  }
};

const removeStorageItem = (key) => {
  try {
    getStorage()?.removeItem(key);
  } catch {
    // Ignore storage failures; the in-tab single-flight still protects requests.
  }
};

const normalizeBaseUrl = (baseUrl = keys.URL) => String(baseUrl || "").replace(/\/+$/, "");

const getChannel = () => {
  if (broadcastChannel || typeof window === "undefined" || typeof window.BroadcastChannel !== "function") {
    return broadcastChannel;
  }

  try {
    broadcastChannel = new window.BroadcastChannel(REFRESH_CHANNEL_NAME);
  } catch {
    broadcastChannel = null;
  }

  return broadcastChannel;
};

const publish = (message) => {
  try {
    getChannel()?.postMessage(message);
  } catch {
    // The storage event remains the fallback for other tabs.
  }
};

const emitWindowEvent = (name, detail) => {
  if (typeof window === "undefined") return;

  try {
    window.dispatchEvent(new CustomEvent(name, { detail }));
  } catch {
    window.dispatchEvent(new Event(name));
  }
};

export const getAccessIssuedAt = () => readNumber(ACCESS_ISSUED_AT_KEY);

export const markAccessTokenIssued = (issuedAt = Date.now()) => {
  try {
    getStorage()?.setItem(ACCESS_ISSUED_AT_KEY, String(issuedAt));
  } catch {
    // The next authenticated request can still refresh through the 401 path.
  }

  emitWindowEvent(ACCESS_REFRESHED_EVENT, { issuedAt });
  return issuedAt;
};

export const clearAccessTokenIssuedAt = () => removeStorageItem(ACCESS_ISSUED_AT_KEY);

export const isAccessTokenNearExpiry = (now = Date.now()) => {
  const issuedAt = getAccessIssuedAt();
  if (!issuedAt) return true;

  return now >= issuedAt + ACCESS_TOKEN_TTL_MS - REFRESH_LEAD_TIME_MS;
};

export const hasAuthenticatedSession = () => {
  try {
    const storage = getStorage();
    if (storage?.getItem("isAuthenticated") === "true" || storage?.getItem("isLoggedIn") === "true") {
      return true;
    }

    const rawRoles = storage?.getItem("userRoles");
    if (!rawRoles) return false;

    const roles = JSON.parse(rawRoles);
    return Array.isArray(roles) ? roles.length > 0 : Boolean(roles);
  } catch {
    return false;
  }
};

export const emitSessionExpired = (reason = "authentication-failed") => {
  try {
    getStorage()?.setItem("isTokenExpired", "true");
  } catch {
    // The session manager still receives the event in this tab.
  }

  emitWindowEvent(SESSION_EXPIRED_EVENT, { reason });
  publish({ type: SESSION_EXPIRED_EVENT, reason });
};

const createOwnerId = () => (
  `${Date.now()}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`
);

const acquireRefreshLock = (owner) => {
  const now = Date.now();
  const existing = readJson(REFRESH_LOCK_KEY);

  if (existing?.owner && existing.expiresAt > now && existing.owner !== owner) {
    return false;
  }

  writeJson(REFRESH_LOCK_KEY, {
    owner,
    expiresAt: now + REFRESH_LOCK_TTL_MS,
  });

  return readJson(REFRESH_LOCK_KEY)?.owner === owner;
};

const releaseRefreshLock = (owner) => {
  const lock = readJson(REFRESH_LOCK_KEY);
  if (lock?.owner === owner) {
    removeStorageItem(REFRESH_LOCK_KEY);
  }

  publish({ type: "refresh-lock-released", owner });
};

const waitForPeerRefresh = (baselineIssuedAt) => new Promise((resolve) => {
  let settled = false;
  let timeoutId;

  const finish = (result) => {
    if (settled) return;
    settled = true;
    window.clearTimeout(timeoutId);
    window.removeEventListener("storage", handleStorage);
    getChannel()?.removeEventListener("message", handleMessage);
    resolve(result);
  };

  const check = () => {
    if (getAccessIssuedAt() > baselineIssuedAt) {
      finish(true);
      return;
    }

    const lock = readJson(REFRESH_LOCK_KEY);
    if (!lock?.expiresAt || lock.expiresAt <= Date.now()) {
      finish(false);
    }
  };

  const handleStorage = (event) => {
    if (event.key === ACCESS_ISSUED_AT_KEY || event.key === REFRESH_LOCK_KEY) {
      check();
    }
  };

  const handleMessage = (event) => {
    if (event.data?.type === "refresh-success" || event.data?.type === "refresh-lock-released") {
      check();
    }
  };

  window.addEventListener("storage", handleStorage);
  getChannel()?.addEventListener("message", handleMessage);
  timeoutId = window.setTimeout(() => finish(getAccessIssuedAt() > baselineIssuedAt), REFRESH_LOCK_TTL_MS + 1000);
  check();
});

const performRefresh = async (baseUrl) => {
  try {
    const response = await fetch(`${normalizeBaseUrl(baseUrl)}${REFRESH_ENDPOINT}`, {
      method: "POST",
      credentials: "include",
    });

    if (!response.ok) return false;

    const issuedAt = markAccessTokenIssued();
    publish({ type: "refresh-success", issuedAt });
    return true;
  } catch {
    return false;
  }
};

export const refreshAuthSession = (baseUrl = keys.URL, { force = true } = {}) => {
  if (refreshPromise) return refreshPromise;

  if (!force && !isAccessTokenNearExpiry()) {
    return Promise.resolve(true);
  }

  const baselineIssuedAt = getAccessIssuedAt();
  const owner = createOwnerId();

  refreshPromise = (async () => {
    if (!acquireRefreshLock(owner)) {
      return waitForPeerRefresh(baselineIssuedAt);
    }

    try {
      return await performRefresh(baseUrl);
    } finally {
      releaseRefreshLock(owner);
    }
  })().finally(() => {
    refreshPromise = null;
  });

  return refreshPromise;
};

export const resetSessionCoordinatorForTests = () => {
  refreshPromise = null;
  try {
    broadcastChannel?.close?.();
  } catch {
    // Ignore test/runtime cleanup failures.
  }
  broadcastChannel = null;
};
